import React from 'react';
import { TrendingUp, Calendar, Users, Plus, AlertCircle } from 'lucide-react';
import './PlanUsage.css';

/**
 * PlanUsage Component
 * Displays current plan, usage statistics, and progress bars
 */
function PlanUsage({ usage, onUpgrade, onBuyAddon }) {
  if (!usage) {
    return (
      <div className="plan-usage-card">
        <div className="loading-state">Loading plan information...</div>
      </div>
    );
  }

  const {
    plan,
    eventsLimit,
    attendeesLimit,
    addonEvents,
    monthlyEvents,
    totalEventsAllowed,
    eventsRemaining,
    usageResetDate,
    events
  } = usage;

  // Calculate usage percentages
  const eventsPercentage = eventsLimit === null 
    ? 0 
    : (monthlyEvents / totalEventsAllowed) * 100;

  // Determine status color based on usage
  const getStatusColor = (percentage) => {
    if (percentage >= 100) return 'danger';
    if (percentage >= 80) return 'warning';
    return 'success';
  };

  const eventsStatus = getStatusColor(eventsPercentage);

  // Format reset date
  const formatResetDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    // Handle both timestamp (number) and date string
    const date = new Date(typeof dateValue === 'number' ? dateValue : dateValue);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Check if approaching limit (80% threshold)
  const isApproachingLimit = eventsPercentage >= 80 && eventsPercentage < 100;
  const isAtLimit = eventsPercentage >= 100;

  return (
    <div className="plan-usage-container">
      {/* Warning Messages */}
      {isApproachingLimit && (
        <div className="alert alert-warning">
          <AlertCircle size={20} />
          <div>
            <strong>Approaching Limit</strong>
            <p>You've used {monthlyEvents} of {totalEventsAllowed} events this month. Consider upgrading or purchasing add-ons.</p>
          </div>
        </div>
      )}

      {isAtLimit && (
        <div className="alert alert-danger">
          <AlertCircle size={20} />
          <div>
            <strong>Event Limit Reached</strong>
            <p>You've reached your monthly event limit. Upgrade your plan or purchase a single event add-on to continue.</p>
          </div>
        </div>
      )}

      {/* Usage Statistics */}
      <div className="usage-stats-grid">
        {/* Events Usage */}
        <div className="usage-stat-card">
          <div className="stat-header">
            <Calendar size={20} />
            <span className="stat-label">Events This Month</span>
          </div>
          <div className="stat-value">
            {monthlyEvents} <span className="stat-limit">/ {eventsLimit === null ? '∞' : totalEventsAllowed}</span>
          </div>
          {eventsLimit !== null && (
            <>
              <div className="progress-bar">
                <div 
                  className={`progress-fill ${eventsStatus}`}
                  style={{ width: `${Math.min(eventsPercentage, 100)}%` }}
                />
              </div>
              <div className="stat-footer">
                {eventsRemaining > 0 ? (
                  <span className="text-success">{eventsRemaining} events remaining</span>
                ) : (
                  <span className="text-danger">No events remaining</span>
                )}
              </div>
            </>
          )}
        </div>

        {/* Add-on Events */}
        {addonEvents > 0 && (
          <div className="usage-stat-card addon-card">
            <div className="stat-header">
              <Plus size={20} />
              <span className="stat-label">Add-on Events</span>
            </div>
            <div className="stat-value">
              {addonEvents} <span className="stat-limit">bonus events</span>
            </div>
            <div className="stat-footer">
              <span className="text-muted">Resets on {formatResetDate(usageResetDate)}</span>
            </div>
          </div>
        )}

        {/* Attendee Limit */}
        <div className="usage-stat-card">
          <div className="stat-header">
            <Users size={20} />
            <span className="stat-label">Attendee Limit</span>
          </div>
          <div className="stat-value">
            {attendeesLimit === null ? '∞' : attendeesLimit.toLocaleString()} <span className="stat-limit">per event</span>
          </div>
          <div className="stat-footer">
            <span className="text-muted">Maximum attendees per event</span>
          </div>
        </div>
      </div>

      {/* Reset Date Info */}
      <div className="reset-info">
        <Calendar size={16} />
        <span>Usage resets on <strong>{formatResetDate(usageResetDate)}</strong></span>
      </div>

      {/* Buy Add-on Button */}
      {plan !== 'Enterprise' && eventsRemaining !== null && eventsRemaining < 3 && (
        <div className="addon-cta">
          <button className="buy-addon-btn" onClick={onBuyAddon}>
            <Plus size={18} />
            Buy Single Event Add-on ($50)
          </button>
          <p className="addon-description">
            Need just one more event? Purchase a single event add-on instead of upgrading.
          </p>
        </div>
      )}

      {/* Recent Events List */}
      {events && events.length > 0 && (
        <div className="recent-events">
          <h3 className="section-title">Events This Month</h3>
          <div className="events-list">
            {events.slice(0, 5).map((event) => (
              <div key={event.id} className="event-item">
                <div className="event-info">
                  <span className="event-name">{event.name}</span>
                  <span className="event-date">
                    {(() => {
                      const dateValue = event.createdAt || event.created_at;
                      if (!dateValue) return 'N/A';
                      const date = new Date(typeof dateValue === 'number' ? dateValue : dateValue);
                      if (isNaN(date.getTime())) return 'N/A';
                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    })()}
                  </span>
                </div>
                <div className="event-attendees">
                  <Users size={14} />
                  <span>{event.attendeeCount} attendees</span>
                </div>
              </div>
            ))}
          </div>
          {events.length > 5 && (
            <div className="events-footer">
              <span className="text-muted">+ {events.length - 5} more events</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PlanUsage;
